/**
 * 视频风格缩略 tile — 2026-06-04
 *
 * 静态样例图(视频专属优先,缺则复用海报库 sampleKey)→ 渐变占位 + 风格名 兜底。
 * (曾试过 Ken-Burns 动图预览,假运动观感差,改回纯静态图。)
 *
 * a11y / 性能(ui-ux-pro-max):loading=lazy + 固定宽高比防 CLS、alt、aria-pressed、
 * cursor-pointer、focus-visible 环;hover 仅图片在 overflow-hidden 内轻微放大,tile 尺寸不变。
 */
import { useState } from "react";
import {
  Box,
  Camera,
  Check,
  Clapperboard,
  Palette,
  PenTool,
  Shapes,
  Sparkles,
  type LucideIcon,
} from "lucide-react";

import { cn } from "@/shared/lib/utils";
import {
  type VideoMediumKey,
  type VideoStyleOption,
  videoStylePlaceholderGradient,
  videoStyleStaticUrl,
} from "../../ui/components/videoStyleOptions";

/**
 * 没有样例图(待补图 / 加载失败)时的设计化兜底:渐变底 + 按媒介的图标 + 柔光,
 * 让卡看起来是「有意为之的封面」而不是一块空白色块(1005:产品三维特写等卡曾只剩纯渐变)。
 * 无媒介(自由发挥)用 Sparkles。
 */
const MEDIUM_ICON: Record<VideoMediumKey, LucideIcon> = {
  "live-action": Camera,
  "cinematic-3d": Box,
  "motion-graphics": Shapes,
  "2d-anim": PenTool,
  painterly: Palette,
  "stop-motion": Clapperboard,
};

function videoStyleFallbackIcon(option: VideoStyleOption): LucideIcon {
  return option.medium ? MEDIUM_ICON[option.medium] : Sparkles;
}

interface Props {
  option: VideoStyleOption;
  selected: boolean;
  onSelect: () => void;
  /** sm:横排紧凑;md:画廊网格(显示 blurb) */
  size?: "sm" | "md";
  className?: string;
}

export function VideoStyleThumb({ option, selected, onSelect, size = "md", className }: Props) {
  const staticUrl = videoStyleStaticUrl(option);
  const [imgFailed, setImgFailed] = useState(false);
  const showImg = !!staticUrl && !imgFailed;

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      title={option.blurb || option.label}
      className={cn(
        "group/vstyle relative flex flex-col overflow-hidden rounded-lg border text-left transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1",
        selected
          ? "border-primary ring-2 ring-primary"
          : "border-border/60 hover:border-primary/50",
        className
      )}
    >
      {/* 16:9 缩略区(视频以横画幅展示动效更直观) */}
      <div
        className="relative w-full overflow-hidden"
        style={{ aspectRatio: "16 / 9", background: videoStylePlaceholderGradient(option.key) }}
      >
        {showImg && (
          <img
            src={staticUrl}
            alt={`${option.label} 风格样例`}
            loading="lazy"
            decoding="async"
            onError={() => setImgFailed(true)}
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-200 group-hover/vstyle:scale-105"
          />
        )}
        {/* ★不在渐变上再写一遍风格名(2026-08-06 走查):下面的标签行永远显示它,
            叠在图位上就是同一个词出现两次(「自由发挥」那张最明显)。
            ★1005:无图时也不能是一块空白色块 —— 渐变上叠柔光 + 媒介图标,当作设计过的封面。 */}
        {!showImg && <ThumbFallback option={option} small={size === "sm"} />}
        {selected && (
          <span className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground shadow">
            <Check className="h-3 w-3" />
          </span>
        )}
      </div>

      <div className={cn("px-1.5 py-1", size === "sm" ? "text-center" : "")}>
        <div
          className={cn(
            "truncate text-xs font-medium",
            selected ? "text-primary" : "text-foreground"
          )}
        >
          {option.label}
        </div>
        {size === "md" && option.blurb && (
          <div className="mt-0.5 line-clamp-1 text-2xs text-muted-foreground">{option.blurb}</div>
        )}
      </div>
    </button>
  );
}

function ThumbFallback({ option, small }: { option: VideoStyleOption; small: boolean }) {
  const Icon = videoStyleFallbackIcon(option);
  return (
    <div
      data-testid="vstyle-fallback"
      aria-hidden
      className="absolute inset-0 flex items-center justify-center"
      style={{
        backgroundImage:
          "radial-gradient(circle at 30% 25%, rgba(255,255,255,0.35), transparent 55%), radial-gradient(circle at 80% 90%, rgba(0,0,0,0.18), transparent 60%)",
      }}
    >
      <span
        className={cn(
          "flex items-center justify-center rounded-full bg-white/20 text-white shadow-sm ring-1 ring-white/30 backdrop-blur-sm",
          small ? "h-7 w-7" : "h-10 w-10"
        )}
      >
        <Icon className={small ? "h-3.5 w-3.5" : "h-5 w-5"} strokeWidth={1.75} />
      </span>
    </div>
  );
}
